import React from 'react';

import styles from '../index.module.css';

import { ReactComponent as Step2 } from 'asset/CustomIcons/icon-2fa-and-risk.svg';
import { ReactComponent as Step4 } from 'asset/CustomIcons/icon-criminal-checks.svg';
import { ReactComponent as Step3 } from 'asset/CustomIcons/icon-passport-id.svg';
// Note: This will be required in the Workflow Phase - 2 👇
import { ReactComponent as Step1 } from 'asset/CustomIcons/icon-workflow-setup.svg';
import { ReactComponent as Step6 } from 'asset/IconsPack/at_dataflow-04.svg';
import { ReactComponent as Step5 } from 'asset/IconsPack/at_settings-01.svg';

import DoNotCollectSsnFallback from '../components/WorkflowSteps/Settings/components/UserInformation/components/Fallbacks/DoNotCollectSsnFallback';
import FullSsnFallback from '../components/WorkflowSteps/Settings/components/UserInformation/components/Fallbacks/FullSsnFallback';
import PartialSsnFallback from '../components/WorkflowSteps/Settings/components/UserInformation/components/Fallbacks/PartialSsnFallback';

export const WORKFLOW_STEPS = {
    SETUP: 'setup',
    TWO_FA_AND_RISK: 'twoFAandRisk',
    IDENTITY: 'identity',
    CRIMINAL_AND_BACKGROUND: 'criminalAndBackground',
    SETTINGS: 'settings',
    SUMMARY: 'summary',
    FINAL: 'final', // will be used in small screens
};

export const STEPPER_DATA = [
    {
        key: WORKFLOW_STEPS.SETUP,
        icon: Step1,
        name: 'Setup',
    },
    {
        key: WORKFLOW_STEPS.TWO_FA_AND_RISK,
        icon: Step2,
        name: '2FA & Risk',
    },
    {
        key: WORKFLOW_STEPS.IDENTITY,
        icon: Step3,
        name: 'Identity',
    },
    {
        key: WORKFLOW_STEPS.CRIMINAL_AND_BACKGROUND,
        icon: Step4,
        name: 'Criminal & Background',
    },
    {
        key: WORKFLOW_STEPS.SETTINGS,
        icon: Step5,
        name: 'Settings',
    },
    {
        key: WORKFLOW_STEPS.SUMMARY,
        icon: Step6,
        name: 'Summary',
    },
];

export const WORKFLOW_TOOLTIP_DATA = {
    welcome: {
        key: 'welcome',
        heading: 'Welcome to Workflow Setup!',
        description: `Follow these steps to build a customized verification process: name your workflow, add a description, and select countries. Next, configure 2FA & Risk, Identity checks, Criminal & Background screenings, and additional Settings. Finally, review everything in the Summary. Click Next to start building your workflow.`,
    },
    twoFA: {
        key: 'twoFA',
        heading: '2FA (Two-Factor Authentication)',
        description:
            'Two-factor authentication (2FA) strengthens security by requiring two forms of verification, like a one-time passcode sent to both email and phone, reducing the risk of unauthorized access.',
    },
    riskAssessment: {
        key: 'riskAssessment',
        heading: 'Risk Assessment',
        description: `Risk Assessment verifies if a user's input data aligns with our proprietary identity graph. An address is required to perform this check, and providing complete address details can help significantly reduce the risk score.`,
    },
    quickVerify: {
        key: 'quickVerify',
        heading: 'Quick Verify',
        description: `Enables eligible users to skip extra identity verification steps—like uploading document proof or answering Knowledge-Based Authentication (KBA) questions—if they successfully complete 2FA and have a low-risk score. Activating this option streamlines the verification process, making it faster and more efficient for trusted users, reducing friction while maintaining security.`,
    },
    govtId: {
        key: 'govtId',
        heading: 'Government ID',
        description: (
            <>
                Verify a Government Photo ID, read with OCR & where the barcode is decoded and
                matched with the data from the document. We also provide our degree of confidence
                that it’s authentic.{' '}
                <a
                    href="https://info.authenticate.com/government-id-verification"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    documentConfiguration: {
        key: 'documentConfiguration',
        heading: 'Document Configuration',
        description: (
            <>
                This selection of documents will be shown to the user when they upload a document.
                <br />
                If the user uploads an incorrect document type, it will be considered an invalid,
                billable attempt.
            </>
        ),
    },
    otherCountryIDs: {
        key: 'otherCountryIDs',
        heading: 'Other Country Documents',
        description: (
            <>
                Users verifying from any country can submit any valid government-issued document.
                <br />
                Granular document type selection for additional countries is coming soon.
            </>
        ),
    },
    onlyUSIds: {
        key: 'onlyUSIds',
        heading: 'Only applicable to US-issued IDs.',
        description: (
            <>
                Select which document types US users can submit for verification. Unselected
                document types will be rejected. At least one document type must be selected.
            </>
        ),
    },
    selfie: {
        key: 'selfie',
        heading: 'Selfie',
        description: (
            <>
                Verify the likeness of a face in two images by comparing photos & receiving an
                instant correlation score. The comparison photo will also be measured for liveness
                using iBeta Level 2 anti-spoofing technology.{' '}
                <a
                    href="https://info.authenticate.com/facial-recognition-liveness-detection"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    kba: {
        key: 'kba',
        heading: 'KBA',
        description: (
            <>
                Verify & authenticate identity with a personal knowledge quiz generated from the
                user’s name, date of birth and occasionally an address. In rare cases a partial or
                full SSN is required.{' '}
                <a
                    href="https://info.authenticate.com/knowledge-based-authentication"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    faov: {
        key: 'faov',
        heading: 'Financial Account Ownership',
        description: (
            <>
                Authenticate a user’s identity by connecting their financial account, and matching
                the registered names. For higher confidence, we recommend enabling the authoritative
                database check as well.{' '}
                <a
                    href="https://info.authenticate.com/financial-account-ownership-verification-usa"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    authDB: {
        key: 'authDB',
        heading: 'Authoritative Database',
        description: (
            <>
                Verify the user’s data in authoritative databases for all US states. This step is
                mandatory if Quick Verify is enabled.{' '}
                <a
                    href="https://info.authenticate.com/authoritative-database-search-usa"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    sevenYearCriminalCheck: {
        key: 'sevenYearCriminalCheck',
        heading: '7 Year criminal Activity check',
        description: (
            <>
                Verify any commercially available criminal activity data that can be found
                nationwide for the last seven years. No pass through fees & an instant turnaround.{' '}
                <a
                    href="https://info.authenticate.com/7-year-criminal-report-usa"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    global_watchlist: {
        key: 'global_watchlist',
        heading: 'Global Watchlist Check',
        description: (
            <>
                The Global Watch List Search helps identify if a person is flagged on any U.S. or
                international sanctions and watch lists, like OFAC or the UN list. If a match is
                found, a detailed report is available.{' '}
                <a
                    href="https://info.authenticate.com/global-watchlist-report"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    sex_offender: {
        key: 'sex_offender',
        heading: 'Sex Offender Check Pro',
        description: (
            <>
                Sex Offender Check Pro helps organizations identify individuals with a history of
                sexual offenses by leveraging an extensive database of over 600,000 records,
                including level 1 offenses. Unlike traditional search tools, it uses advanced
                techniques to verify and enhance incomplete data, ensuring more accurate results.
                With superior matching capabilities, it minimizes false positives and false
                negatives, providing timely and actionable insights for informed decision-making. If
                a match is found, a detailed report is available.{' '}
                <a
                    href="https://info.authenticate.com/sex-offender-check"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    education: {
        key: 'education',
        heading: 'Education',
        description: (
            <>
                Verify School Name & Type, Campus Name, School Town & Country, Start Date & End
                Date, Degree Title, if they are currently attending and/or if they've completed
                their degree successfully - no passthrough fees.{' '}
                <a
                    href="https://info.authenticate.com/education-verification"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    employment: {
        key: 'employment',
        heading: 'Employment',
        description: (
            <>
                Verify Employer Name, Town, State & Country, Job Title, Date Employed From & To,
                Supervisor Name, if they are currently employed & what their contract type is - no
                passthrough fees. If unavailable by database the average turnaround time is 48 hours
                to get a response from the source.{' '}
                <a
                    href="https://info.authenticate.com/employment-verification"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    professionalLicense: {
        key: 'professionalLicense',
        heading: 'Professional License',
        description: (
            <>
                Verify License Title, Organization & Number, Start & End Date, State & Country. If
                unavailable by database the average turnaround time is 48 hours to get a response
                from the source.{' '}
                <a
                    href="https://info.authenticate.com/professional-license-verification"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tooltipCta}
                >
                    Learn More
                </a>
            </>
        ),
    },
    redirectURL: {
        key: 'redirectURL',
        heading: 'Redirect URL',
        description: `Please enter a valid SSL secured URL. If provided, the user will be redirected to the specified URL once the verification process is complete.`,
    },
    user_information_form: {
        key: 'user_information_form',
        heading: 'User Information Form',
        description: (
            <>
                Easily configure and collect essential user data for secure and accurate identity
                verification.
                <br />
                <br />
                Customize SSN requirements to align with your workflow and compliance needs while
                ensuring a seamless experience for users.
                <br />
                <br />
                Mandatory fields are pre-selected based on the checks chosen earlier and cannot be
                modified; however, additional mandatory fields can be added as needed, even if they
                are not part of the workflow requirements.
            </>
        ),
    },
    name: {
        key: 'name',
        heading: 'Full Name',
        description: `The user's legal full name, always required for accurate identity verification`,
    },
    dob: {
        key: 'dob',
        heading: 'Date of Birth',
        description: `Used to confirm the user's age and identity. Always required for verification.`,
    },
    email: {
        key: 'email',
        heading: 'Email',
        description:
            'Required if 2FA is enabled in the workflow. Used for authentication, verification, and security notifications.',
    },
    phone_number: {
        key: 'phone_number',
        heading: 'Phone Number',
        description:
            'Mandatory if 2FA is enabled in the workflow. Used for authentication, SMS verification, and fraud prevention.',
    },
    address: {
        key: 'address',
        heading: 'Address',
        description:
            'Required if Risk Assessment is configured in the workflow. If not pre-selected, making it mandatory can help refine identity matches in authoritative databases when Name and DOB alone are insufficient.',
    },
};

export const CHECKBOX_TOOLTIP_MESSAGES = {
    DISABLED_GOVT_ID: 'Available only with Government ID check',
    ONLY_US: 'Method only available for US',
    DISABLED_KBA: 'Available only with KBA check',
};

export const VERIFICATION_ATTEMPT_COUNT = {
    DEFAULT: 2,
    MAX_ATTEMPT: 10,
    MIN_ATTEMPT: 1,
};

export const WORKFLOW_CHECKS = {
    GOVT_ID: 'govt_id',
    KBA: 'kba',
    AUTH_DB: 'authoritative_db',
    BAV: 'bank',
    CRIMINAL_CHECK: 'has_criminal_record', //deprecated
    SEVEN_YEAR_CRIMINAL_CHECK: 'seven_year_criminal_report',
    PROFESSIONAL_LICENSE: 'professional_license',
    EDUCATION: 'education',
    EMPLOYMENT: 'employment',
    QUICK_VERIFY: 'quick_verify',
    '2FA': '2fa',
    RISK_SCORE: 'risk_score',
    SOC: 'sex_offender',
    GWC: 'global_watchlist',
};

export const MODIFIED_WORKFLOW_CHECKS = {
    [WORKFLOW_CHECKS.GOVT_ID]: 'Government ID',
    [WORKFLOW_CHECKS.KBA]: 'KBA',
    [WORKFLOW_CHECKS.AUTH_DB]: 'Authoritative database',
    [WORKFLOW_CHECKS.SEVEN_YEAR_CRIMINAL_CHECK]: '7 Year Criminal Activity Check',
    [WORKFLOW_CHECKS.BAV]: 'Financial Account Ownership',
    [WORKFLOW_CHECKS.PROFESSIONAL_LICENSE]: 'Professional License',
    [WORKFLOW_CHECKS.EDUCATION]: 'Education',
    [WORKFLOW_CHECKS.EMPLOYMENT]: 'Employment',
    GOVT_ID_WITH_SELFIE: 'Government ID with Selfie',
    [WORKFLOW_CHECKS['2FA']]: '2FA',
    [WORKFLOW_CHECKS.QUICK_VERIFY]: 'Quick Verify',
    [WORKFLOW_CHECKS.RISK_SCORE]: 'Risk Assessment',
    [WORKFLOW_CHECKS.SOC]: 'Sex Offender Check',
    [WORKFLOW_CHECKS.GWC]: 'Global Watchlist Check',
};

// Identity Proof methods governed by the all/any (verificationType) configuration.
// Order matches the checkbox order in the Identity Proof step.
export const IDENTITY_PROOF_METHODS = [
    { field: 'govt_id', label: 'Government ID' },
    { field: 'kba', label: 'KBA' },
    { field: 'faov', label: 'Financial Account Ownership' },
];

export const DEFAULT_WORKFLOW_PAGE_SIZE = 50;

export const WORKFLOW_PAGE_TYPES = {
    CREATE: 'CREATE WORKFLOW',
    EDIT: 'EDIT WORKFLOW',
    VIEW: 'VIEW WORKFLOW',
};

export const COUNTRY_OPTIONS = {
    USA: {
        label: 'United States of America',
        value: 'USA',
    },
    ALL: {
        label: 'All Countries',
        value: 'all',
    },
};

export const WORKFLOW_POPUP_TYPES = {
    DISCARD: 'DISCARD',
    SAVE: 'SAVE',
    DELETE: 'DELETE',
};

export const WORKFLOW_POPUP_DATA = {
    WORKFLOW_POPUP_TITLE: {
        [WORKFLOW_POPUP_TYPES.DISCARD]: 'Discard Changes?',
        [WORKFLOW_POPUP_TYPES.SAVE]: 'Save Changes?',
    },
    WORKFLOW_POPUP_DESCRIPTION: {
        [WORKFLOW_POPUP_TYPES.DISCARD]:
            'Changes to the workflow haven’t been saved. Discard changes?',
        [WORKFLOW_POPUP_TYPES.SAVE]: 'Changes to the workflow will be saved.',
    },
    WORKFLOW_POPUP_SECONDARY_BUTTON_TEXT: {
        [WORKFLOW_POPUP_TYPES.DISCARD]: 'Keep editing',
        [WORKFLOW_POPUP_TYPES.SAVE]: 'Cancel',
    },
    WORKFLOW_POPUP_PRIMARY_BUTTON_TEXT: {
        [WORKFLOW_POPUP_TYPES.DISCARD]: 'Discard',
        [WORKFLOW_POPUP_TYPES.SAVE]: 'Save',
    },
};

export const WORKFLOW_STEP_ORDER = [
    WORKFLOW_STEPS.SETUP,
    WORKFLOW_STEPS.TWO_FA_AND_RISK,
    WORKFLOW_STEPS.IDENTITY,
    WORKFLOW_STEPS.CRIMINAL_AND_BACKGROUND,
    WORKFLOW_STEPS.SETTINGS,
    WORKFLOW_STEPS.SUMMARY,
];

export const WORKFLOW_TOASTER_MESSAGES = {
    CREATE_SUCCESS: 'Workflow created successfully.',
    EDIT_SUCCESS: 'Changes saved.',
};

export const DEFAULT_CHECK_VALUES = {
    IDENTITY: {
        kba: false,
        show_kba_result: false,
        faov: false,
        auth_db: false,
        allowedDocTypes: [],
    },
    CRIMINAL: {
        criminal_check_indicator: false,
        seven_year_criminal_check: false,
        global_watchlist: false,
        sex_offender: false,
    },
    BACKGROUND: {
        education: false,
        employment: false,
        professional_license: false,
    },
};

export const SSN_CONFIGURATION_TYPES = {
    DO_NOT_COLLECT_SSN: 'do_not_collect_ssn',
    COLLECT_PARTIAL_SSN: 'partial',
    COLLECT_FULL_SSN: 'full',
};

export const DEFAULT_SSN_CONFIGURATION = {
    NO_SSN_CONFIGURED: {
        enabled: false,
        type: null,
        fallback: [],
    },
    [SSN_CONFIGURATION_TYPES.DO_NOT_COLLECT_SSN]: {
        enabled: true,
        type: null,
        fallback: [
            SSN_CONFIGURATION_TYPES.COLLECT_FULL_SSN,
            SSN_CONFIGURATION_TYPES.COLLECT_PARTIAL_SSN,
        ],
    },
    [SSN_CONFIGURATION_TYPES.COLLECT_PARTIAL_SSN]: {
        enabled: true,
        type: SSN_CONFIGURATION_TYPES.COLLECT_PARTIAL_SSN,
        fallback: [SSN_CONFIGURATION_TYPES.COLLECT_FULL_SSN],
    },
    [SSN_CONFIGURATION_TYPES.COLLECT_FULL_SSN]: {
        enabled: true,
        type: SSN_CONFIGURATION_TYPES.COLLECT_FULL_SSN,
        fallback: [],
    },
};

export const SSN_SETTINGS_OPTIONS = [
    {
        type: SSN_CONFIGURATION_TYPES.DO_NOT_COLLECT_SSN,
        label: 'Do Not Collect SSN',
        FallbackComponent: DoNotCollectSsnFallback,
    },
    {
        type: SSN_CONFIGURATION_TYPES.COLLECT_PARTIAL_SSN,
        label: 'Collect Partial SSN (Recommended)',
        FallbackComponent: PartialSsnFallback,
    },
    {
        type: SSN_CONFIGURATION_TYPES.COLLECT_FULL_SSN,
        label: 'Collect Full SSN',
        FallbackComponent: FullSsnFallback,
    },
];

export const ALL_MANDATORY_FIELDS = [
    { key: 'name', label: 'Full Name' },
    { key: 'dob', label: 'Date of Birth' },
    { key: 'email', label: 'Email' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
];

export const ALWAYS_MANDATORY_FIELDS = ['name', 'dob', 'email'];

export const CATEGORIZED_WORKFLOW_CHECKS = {
    '2FA': [WORKFLOW_CHECKS['2FA']],
    Risk: [WORKFLOW_CHECKS.RISK_SCORE],
    Identity: [
        WORKFLOW_CHECKS.GOVT_ID,
        WORKFLOW_CHECKS.KBA,
        WORKFLOW_CHECKS.AUTH_DB,
        WORKFLOW_CHECKS.BAV,
        WORKFLOW_CHECKS.QUICK_VERIFY,
    ],
    Criminal: [WORKFLOW_CHECKS.SEVEN_YEAR_CRIMINAL_CHECK, WORKFLOW_CHECKS.GWC, WORKFLOW_CHECKS.SOC],
    Background: [
        WORKFLOW_CHECKS.EDUCATION,
        WORKFLOW_CHECKS.EMPLOYMENT,
        WORKFLOW_CHECKS.PROFESSIONAL_LICENSE,
    ],
};

export const CONFIGURABLE_MANDATORY_FIELDS = ['phone_number', 'address'];

export const ALLOWED_DOCUMENT_TYPES = [
    {
        label: 'Identification Card',
        value: 'IDENTIFICATION CARD',
    },
    {
        label: 'Drivers License',
        value: 'DRIVERS LICENSE',
    },
    {
        label: 'Visa',
        value: 'VISA',
    },
    {
        label: 'Passport',
        value: 'PASSPORT',
    },
    {
        label: 'Residence Document',
        value: 'RESIDENCE DOCUMENT',
    },
    {
        label: 'Voter Document',
        value: 'VOTER DOCUMENT',
    },
    {
        label: 'Military Card',
        value: 'MILITARY',
    },
];

export const SANDBOX_DISABLED_IDENTITY_PROOF = 'This identity proof not available in sandbox mode.';
export const SANDBOX_TOOLTIP_CONTENT = 'This check is currently not available in sandbox mode.';
